import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const AdminContext = createContext(null);
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "gfn_admin_token";

export const AdminProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setVerifying(true);
    fetch(`${BACKEND}/api/admin/verify`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!cancelled && !r.ok) {
          setToken("");
          localStorage.removeItem(STORAGE_KEY);
        }
      })
      .catch(() => {})
      .finally(() => !cancelled && setVerifying(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = useCallback(async (password) => {
    const res = await fetch(`${BACKEND}/api/admin/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) return { ok: false, error: "Invalid password" };
    const data = await res.json();
    setToken(data.token);
    localStorage.setItem(STORAGE_KEY, data.token);
    return { ok: true };
  }, []);

  const logout = useCallback(() => {
    setToken("");
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const authFetch = useCallback(
    (url, opts = {}) => {
      const isFormData = typeof FormData !== "undefined" && opts.body instanceof FormData;
      const baseHeaders = {
        ...(opts.headers || {}),
        Authorization: `Bearer ${token}`,
      };
      // Only default JSON content-type when the caller hasn't provided a body
      // that browsers must set the boundary/content-type for (FormData).
      if (!isFormData && !baseHeaders["Content-Type"]) {
        baseHeaders["Content-Type"] = "application/json";
      }
      return fetch(`${BACKEND}${url}`, {
        ...opts,
        headers: baseHeaders,
      });
    },
    [token]
  );

  return (
    <AdminContext.Provider value={{ token, isAdmin: !!token, verifying, login, logout, authFetch }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error("useAdmin must be inside AdminProvider");
  return ctx;
};
